import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { BdcStockDay } from "../../../types/bdc.type";
import { pcs, thaiDate } from "../format";
import { STOCK_SIGNER_MAX_LENGTH } from "./calc";

/**
 * ปิดวัน — ผู้จัดทำ + ผู้ตรวจสอบ (as on the paper form), then the server snapshots every system
 * figure and locks the day. Mismatched counts and ขาด are warnings only: closing is allowed.
 */
export default function CloseDialog({
  open,
  day,
  busy,
  onClose,
  onConfirm,
}: Readonly<{
  open: boolean;
  day: BdcStockDay;
  busy: boolean;
  onClose: () => void;
  onConfirm: (preparedBy: string, checkedBy: string) => void;
}>) {
  const [preparedBy, setPreparedBy] = useState(day.prepared_by_name ?? "");
  const [checkedBy, setCheckedBy] = useState(day.checked_by_name ?? "");

  useEffect(() => {
    if (open) {
      setPreparedBy(day.prepared_by_name ?? "");
      setCheckedBy(day.checked_by_name ?? "");
    }
  }, [open, day.prepared_by_name, day.checked_by_name]);

  const mismatched = day.items.filter((item) => item.is_count_mismatch);
  const short = day.items.filter((item) => item.short > 0);
  const uncounted = day.items.filter((item) => item.leftover === null);
  const valid =
    preparedBy.trim() !== "" &&
    checkedBy.trim() !== "" &&
    preparedBy.trim().length <= STOCK_SIGNER_MAX_LENGTH &&
    checkedBy.trim().length <= STOCK_SIGNER_MAX_LENGTH;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>ปิดวัน {thaiDate(day.work_date)}</DialogTitle>
      <DialogContent>
        <Stack gap={1.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            หลังปิดวัน ตัวเลขของระบบจะถูกเก็บไว้และแก้ไม่ได้ · "เหลือ" ของวันนี้เป็น "ยกมา" ของวันถัดไป
            (สินค้าที่ไม่ได้นับเหลือ ใช้ยอดเกินแทน)
          </Typography>
          {mismatched.length > 0 && (
            <Alert severity="warning">
              เหลือ + ชำรุด + ขาย ไม่เท่ากับเกิน {mismatched.length} รายการ: {mismatched.map((i) => i.name).join(", ")}
            </Alert>
          )}
          {short.length > 0 && (
            <Alert severity="warning">
              ขาด {pcs(day.totals.short)} ชิ้น ใน {short.length} สินค้า
            </Alert>
          )}
          {uncounted.length > 0 && (
            <Alert severity="info">ยังไม่ได้นับ "เหลือ" {uncounted.length} สินค้า — ยกมาของพรุ่งนี้จะใช้ยอดเกิน</Alert>
          )}
          <TextField
            label="ผู้จัดทำ"
            size="small"
            required
            value={preparedBy}
            onChange={(e) => setPreparedBy(e.target.value)}
            slotProps={{ htmlInput: { maxLength: STOCK_SIGNER_MAX_LENGTH } }}
          />
          <TextField
            label="ผู้ตรวจสอบ"
            size="small"
            required
            value={checkedBy}
            onChange={(e) => setCheckedBy(e.target.value)}
            slotProps={{ htmlInput: { maxLength: STOCK_SIGNER_MAX_LENGTH } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          disabled={!valid}
          loading={busy}
          onClick={() => onConfirm(preparedBy.trim(), checkedBy.trim())}
        >
          ปิดวัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
